import { motion } from "framer-motion";
import { Building2, ClipboardList, Truck, Users } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { openWhatsApp } from "@/lib/whatsapp";

const FLOW = [
  { icon: Building2, label: "La RSA segnala i residenti" },
  { icon: ClipboardList, label: "Vengono organizzati gli specialisti" },
  { icon: Truck, label: "L’infermiere arriva con il kit" },
  { icon: Users, label: "Vengono visitati più residenti" },
];

const PLANS = [
  { name: "Visita occasionale", desc: "Un intervento singolo per esigenze puntuali." },
  { name: "Giornata specialistica", desc: "Più residenti visitati nella stessa giornata.", featured: true },
  { name: "Accordo continuativo", desc: "Presenza programmata e ricorrente nella struttura." },
];

export default function RSASection() {
  return (
    <section id="rsa" className="bg-[#F6F4FA] py-20 sm:py-28" data-testid="rsa-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-[#5B2C91]"
          >
            <span className="h-2 w-2 rounded-full bg-[#D4F84A]" />
            RSA e strutture
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="font-heading mt-5 text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#1F152E]"
            data-testid="rsa-title"
          >
            La specialistica arriva nella struttura soltanto quando serve
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-5 text-base sm:text-lg leading-relaxed text-[#4A4059]"
          >
            L’infermiere porta il kit mobile, collega i residenti ai medici e utilizza lo stesso
            TytoCare per le visite successive.
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 overflow-hidden rounded-[2rem] lg:rounded-[2.5rem] shadow-[0_30px_80px_rgba(91,44,145,0.18)] ring-1 ring-white/60"
        >
          <img
            src="/generated/rsa-scene.png"
            alt="Infermiere in una struttura assiste un residente anziano con TytoCare mentre il medico si collega in diretta sul tablet"
            className="w-full h-[300px] sm:h-[420px] lg:h-[500px] object-cover object-center"
            data-testid="rsa-scene-image"
          />
        </motion.div>

        {/* Flow */}
        <div className="relative mt-14">
          <div className="hidden lg:block absolute top-8 left-[8%] right-[8%] h-0.5 bg-gradient-to-r from-transparent via-[#5B2C91]/30 to-transparent" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {FLOW.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 22 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                  className="relative flex flex-col items-center text-center"
                  data-testid="rsa-flow-step"
                >
                  <span className="relative z-10 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-white text-[#5B2C91] ring-1 ring-[#5B2C91]/10 shadow-[0_10px_30px_rgba(91,44,145,0.1)]">
                    <Icon className="h-7 w-7" />
                    <span className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-[#5B2C91] text-xs font-bold text-white">
                      {i + 1}
                    </span>
                  </span>
                  <p className="mt-4 text-sm font-medium leading-snug text-[#3B2F4C]">{f.label}</p>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Plans */}
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-5">
          {PLANS.map((p, i) => (
            <motion.div
              key={p.name}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className={`rounded-3xl p-7 transition-transform duration-300 hover:-translate-y-1.5 ${
                p.featured
                  ? "bg-[#5B2C91] shadow-[0_24px_60px_rgba(91,44,145,0.3)]"
                  : "bg-white ring-1 ring-[#5B2C91]/8 shadow-[0_8px_30px_rgb(91,44,145,0.05)]"
              }`}
              data-testid="rsa-plan-card"
            >
              <span className={`font-heading text-sm font-bold ${p.featured ? "text-[#D4F84A]" : "text-[#5B2C91]"}`}>
                0{i + 1}
              </span>
              <h3 className={`font-heading mt-3 text-xl font-semibold ${p.featured ? "text-white" : "text-[#1F152E]"}`}>
                {p.name}
              </h3>
              <p className={`mt-2 text-sm leading-relaxed ${p.featured ? "text-white/80" : "text-[#6B6280]"}`}>
                {p.desc}
              </p>
            </motion.div>
          ))}
        </div>

        <div className="mt-10">
          <button
            onClick={() => openWhatsApp("Buongiorno, rappresento una struttura/RSA e vorrei ricevere maggiori informazioni sul servizio TytoCare.")}
            className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-[#5B2C91] px-7 py-4 text-base font-semibold text-white shadow-[0_16px_40px_rgba(91,44,145,0.28)] hover:bg-[#4A2277] hover:-translate-y-0.5 transition-[transform,background-color] duration-200"
            data-testid="rsa-cta"
          >
            <FaWhatsapp className="h-5 w-5" />
            Richiedi una dimostrazione
          </button>
        </div>
      </div>
    </section>
  );
}
